import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import { Redirect } from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InvalidGpxFileInformation from '../components/content/informations/InvalidGpxFileInformation';
import LoadingView from '../components/content/LoadingView';
import GpxMediaList from '../components/content/lists/GpxMediaList';
import withRequestHandler from '../components/hoc/withRequestHandler';

class EventGpsSearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      extendedSearchSelectedTab: 2,
      extendedSearchExpand: true,
      showInvalidGpxFileInformation: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { gpxFileIdFromUrl } = this.props;
    if (gpxFileIdFromUrl !== prevProps.gpxFileIdFromUrl) {
      this._refetchMediaForNewGpxFile(gpxFileIdFromUrl);
      scrollToComponent(this.gpxResultsView);
    }
  }

  handleExtendedSearchTabChanged = (event, value) => {
    this.setState({
      extendedSearchSelectedTab: value,
      extendedSearchExpand: true,
    });
  };

  handleExtendedSearchExpand = () => {
    this.setState(prevState => {
      const {
        extendedSearchExpand: prevExpand,
        extendedSearchSelectedTab: prevSelectedTab,
      } = prevState;

      return {
        extendedSearchExpand: !prevExpand,
        // if the extended search changes from open to close it is important to reset
        // the selected tab to its default. Otherwise the media list will not be rendered.
        extendedSearchSelectedTab: prevExpand ? 2 : prevSelectedTab,
      };
    });
  };

  handleDeepSearchBtnClicked = () => {
    this.scrollToEventOnlineView(0);
  };

  handleCategorySearchBtnClicked = () => {
    this.scrollToEventOnlineView(1);
  };

  handleGpsSearchBtnClicked = () => {
    this.scrollToEventOnlineView(2);
  };

  scrollToEventOnlineView(tabIndex) {
    scrollToComponent(this.eventOnlineView);
    this.setState({
      extendedSearchExpand: true,
      extendedSearchSelectedTab: tabIndex,
    });
  }

  _refetchMediaForNewGpxFile = newGpxFileId => {
    this.props.onRequestStart();
    this.setState({ showInvalidGpxFileInformation: false });
    this.props.relay.refetch({ gpxFileId: newGpxFileId }, null, err => {
      if (err) {
        this.props.onRequestStop();
        this.setState({ showInvalidGpxFileInformation: true });
      } else {
        this.props.onRequestStop();
      }
    });
  };

  render() {
    const {
      eventHasSecondFactor,
      isLoading,
      viewer,
      snFromUrl,
      gpxFileIdFromUrl,
      customerKey,
    } = this.props;
    const { event } = viewer;

    if (event.state === 'reminder') {
      return <Redirect to={`/event/${event.sgId}`} />;
    }

    const participantMediaCount = event.participant
      ? event.participant.media.mediaInfo.count
      : 0;

    const {
      extendedSearchSelectedTab,
      extendedSearchExpand,
      showInvalidGpxFileInformation,
    } = this.state;

    return (
      <div className="container-960 container-page">
        <EventOnlineView
          ref={view => (this.eventOnlineView = view)}
          onlineEvent={event}
          urlSn={snFromUrl}
          customerKey={customerKey}
          extendedSearchSelectedTab={extendedSearchSelectedTab}
          extendedSearchExpand={extendedSearchExpand}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={event.gps}
          hasFaceSearchTab={event.searchByFaceAsFeature && (!event.searchByFace || participantMediaCount !== 0)}
          onExtendedSearchTabChange={this.handleExtendedSearchTabChanged}
          onExtendedSearchExpand={this.handleExtendedSearchExpand}
        />
        <hr className="col-xs-16 col-sm-12" ref={view => (this.gpxResultsView = view)}/>
        {isLoading && <LoadingView />}
        {!isLoading &&
          !showInvalidGpxFileInformation &&
          event.searchMediaByGpx.mediaInfo.count !== 0 &&
          extendedSearchSelectedTab === 2 && (
            <GpxMediaList
              eventHasSearchByFace={event.searchByFace ? true : false}
              eventHasSecondFactor={eventHasSecondFactor}
              eventId={event.sgId}
              startnumber={snFromUrl}
              gpxFileId={gpxFileIdFromUrl}
              gpxFileMedia={event.searchMediaByGpx}
              offer={event}
              participantMediaCount={participantMediaCount}
            />
          )}
        {!isLoading &&
          (showInvalidGpxFileInformation ||
            event.searchMediaByGpx.mediaInfo.count === 0) &&
          extendedSearchSelectedTab === 2 && <InvalidGpxFileInformation />}
        <Faq onFindMore={() => this.scrollToEventOnlineView(0)} />
      </div>
    );
  }
}

EventGpsSearchPage.propTypes = {
  customerKey: PropTypes.string,
  eventHasSearchByFace: PropTypes.bool,
  eventHasSecondFactor: PropTypes.bool,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  gpxFileIdFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
};

export { EventGpsSearchPage };

export default createRefetchContainer(
  withRequestHandler(EventGpsSearchPage),
  {
    viewer: graphql`
      fragment EventGpsSearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl

          participant(startnumber: $startnumber, secondFactor: $secondFactor) @skip(if: $noStartnumber){
            media {
              mediaInfo {
                count
              }
            }
            startnumber
          }
          searchMediaByGpx(gpxFileId: $gpxFileId) {
            mediaInfo {
              count
            }
            ...GpxMediaList_gpxFileMedia
          }
          secondFactorType {
            type
          }
          searchByFace
          searchByFaceAsFeature
          smartSearch
          state
          ...EventOnlineView_onlineEvent
          ...GpxMediaList_offer
        }
      }
    `,
  },
  graphql`
    query EventGpsSearchPageRefetchQuery(
      $eventId: ID!
      $language: String
      $startnumber: ID!
      $gpxFileId: ID!
      $count: Int!
      $cursor: String
      $secondFactor: String
      $individualPart: String
      $noStartnumber: Boolean
    ) {
      viewer {
        ...EventGpsSearchPage_viewer
      }
    }
  `
);
